/**
 * Responsibility: the search form's working copy, and what counts as a filter.
 *
 * **Lifted out of the controls because two controls now share it.** The
 * query box and the filter popover are separate things on the toolbar - one
 * is the question, the other narrows it - but they submit together, so
 * neither can own the state. The page holds one draft and hands both of them
 * a slice.
 *
 * **A filter is anything that is not the phrase or the mode.** The badge on
 * the filter button counts these, and that count is the only defence against
 * the thing a popover makes possible: a bound left on last week, hidden,
 * quietly making a search look like it found nothing. So the count lives
 * here, beside the fields it counts, rather than being maintained by eye at
 * the call site.
 */
import { useEffect, useState } from 'react';
import type { MarketCategory, MarketMode, MarketQuery } from '../../api/client';

export type SearchDraft = {
  q: string;
  mode: MarketMode;
  category: MarketCategory | '';
  maxRaw: string;
  minValue: string;
  minYear: string;
  maxYear: string;
  setContains: string;
  gradedOnly: boolean;
};

export const EMPTY_DRAFT: SearchDraft = {
  q: '',
  mode: 'BEST_MATCH',
  category: '',
  maxRaw: '',
  minValue: '',
  minYear: '',
  maxYear: '',
  setContains: '',
  gradedOnly: false,
};

/** The draft a submitted query came from, so the form shows what is on screen. */
export function draftFrom(query: MarketQuery | null): SearchDraft {
  if (!query) return EMPTY_DRAFT;
  return {
    q: query.q,
    mode: query.mode,
    category: query.category ?? '',
    maxRaw: query.maxRaw ?? '',
    minValue: query.minValue ?? '',
    minYear: query.minYear ?? '',
    maxYear: query.maxYear ?? '',
    setContains: query.setContains ?? '',
    gradedOnly: query.gradedOnly ?? false,
  };
}

/** The draft as the hub is asked for it. */
export function queryFrom(draft: SearchDraft): MarketQuery {
  return { ...draft, q: draft.q.trim() };
}

/** Everything but the phrase and the mode, cleared. */
export function withoutFilters(draft: SearchDraft): SearchDraft {
  return { ...EMPTY_DRAFT, q: draft.q, mode: draft.mode };
}

/**
 * How many filters are set.
 *
 * The phrase is not one - it is the search. Nor is the mode: every search
 * has one, so counting it would mean the badge never read zero and stopped
 * meaning anything.
 */
export function activeFilterCount(draft: SearchDraft): number {
  return [
    draft.category,
    draft.maxRaw,
    draft.minValue,
    draft.minYear,
    draft.maxYear,
    draft.setContains,
    draft.gradedOnly ? 'on' : '',
  ].filter(Boolean).length;
}

/**
 * A draft seeded from the page's query, and re-seeded when that changes.
 *
 * Re-seeding matters when a saved search is run: the page hands down a
 * different query and the form has to follow it, or the box says one thing
 * while the table shows another.
 */
export function useSearchDraft(
  initial: MarketQuery | null,
): [SearchDraft, (next: SearchDraft) => void] {
  const [draft, setDraft] = useState<SearchDraft>(() => draftFrom(initial));

  useEffect(() => {
    if (initial) setDraft(draftFrom(initial));
  }, [initial]);

  return [draft, setDraft];
}
